import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import RTButton from '../common/RTButton';
import RTSpinner from '../common/RTSpinner';
import UpdateComplaintStatusModal from '../complaints/UpdateComplaintStatusModal';
import AssignComplaintModal from '../complaints/AssignComplaintModal';
import './StaffDashboard.css';
import tenantStorage from '../../utils/tenantStorage';

const StaffDashboard = ({ data, onDataRefresh }) => {
  const navigate = useNavigate();
  const [staffName, setStaffName] = useState('');
  const [showUpdateModal, setShowUpdateModal] = useState(false);
  const [updateAction, setUpdateAction] = useState(null);
  const [selectedComplaint, setSelectedComplaint] = useState(null);
  const [assignModalOpen, setAssignModalOpen] = useState(false);
  const [selectedAssignComplaint, setSelectedAssignComplaint] = useState(null);

  useEffect(() => {
    // Get staff name from localStorage
    const name = tenantStorage.getItem('staffName') || 'Staff';
    setStaffName(name);
  }, []);

  // Get assigned complaints from dashboard data, limit to 5 for display
  const assignedComplaints = data?.recent_complaints ? data.recent_complaints.slice(0, 5) : [];
  
  // Get unassigned complaints from dashboard data, limit to 5 for display
  const unassignedComplaints = data?.unassigned_complaints ? data.unassigned_complaints.slice(0, 5) : [];

  const handleComplaintClick = (complaintId) => {
    navigate(`/complaints/view/${complaintId}`);
  };

  const handleViewAllComplaints = () => {
    navigate('/complaints/my-assigned');
  };

  const handleViewAllUnassigned = () => {
    navigate('/complaints/view?status=open&assigned_to=null');
  };

  const handleAssignToMe = (e, complaint) => {
    e.stopPropagation();
    setSelectedAssignComplaint({ ...complaint, mode: 'assignSelf' });
    setAssignModalOpen(true);
  };

  const handleAssignSuccess = () => {
    // Refresh dashboard data
    if (onDataRefresh) {
      onDataRefresh();
    }
  };

  const getStatusButton = (complaint) => {
    if (!complaint || !complaint.status) {
      return <span className="complaint-status">No Status</span>;
    }

    const handleButtonClick = (e, action, complaint) => {
      e.stopPropagation();
      setSelectedComplaint(complaint);
      setUpdateAction(action);
      setShowUpdateModal(true);
    };

    switch (complaint.status) {
      case 'open':
        return (
          <RTButton 
            variant="primary" 
            size="small"
            onClick={(e) => handleButtonClick(e, 'start', complaint)}
          >
            Start Work
          </RTButton>
        );
      case 'in_progress':
        return (
          <RTButton 
            variant="success" 
            size="small"
            onClick={(e) => handleButtonClick(e, 'resolve', complaint)}
          >
            Mark Resolved
          </RTButton>
        );
      case 'resolved':
        return (
          <span className="complaint-status status-resolved">
            Resolved
          </span>
        );
      default:
        return (
          <span className={`complaint-status status-${complaint.status.toLowerCase().replace(' ', '-')}`}>
            {complaint.status}
          </span>
        );
    }
  };

  if (!data) {
    return <RTSpinner message="Loading dashboard..." fullScreen={false} />;
  }

  return (
    <div>
      {/* Title Row with Salutation and Refresh Button */}
      <div className="rt-title-row">
        <h2 className="rt-title">Hello, {staffName}!</h2>
        <div className="rt-actions">
          <RTButton 
            variant="primary"
            onClick={() => {
              if (onDataRefresh) {
                onDataRefresh();
              } else {
                window.location.reload();
              }
            }}
            title="Refresh Dashboard"
            className="refresh-button"
          >
            <span className="refresh-icon">🔄</span>
            <span className="refresh-text">Refresh</span>
          </RTButton>
        </div>
      </div>

      <div className="rt-main-section">
        {/* Metrics Section */}
        <div className="dashboard-metrics">
          <div className="metrics-card">
            <h4 className="metrics-heading">Complaint Stats</h4>
            <div className="metrics-row">
              <div className="metric-item">
                <div className="metric-pill total-assigned">
                  <div className="metric-number">{data.total_assigned_complaints || 0}</div>
                  <div className="metric-label">Total Assigned</div>
                </div>
              </div>
              <div className="metric-item">
                <div className="metric-pill resolved-today">
                  <div className="metric-number">{data.complaints_resolved_today || 0}</div>
                  <div className="metric-label">Resolved Today</div>
                </div>
              </div>
              <div className="metric-item">
                <div className="metric-pill resolved-month">
                  <div className="metric-number">{data.complaints_resolved_this_month || 0}</div>
                  <div className="metric-label">Resolved This Month</div>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Assigned Complaints Section - Full Width */}
        <div className="dashboard-section full-width">
          <div className="section-header">
            <h4 className="section-title">Assigned Complaints ({data.total_assigned_complaints})</h4>
            <button 
              className="view-all-link" 
              onClick={handleViewAllComplaints}
            >
              View All →
            </button>
          </div>

          <div className="complaints-table">
            {assignedComplaints.length === 0 ? (
              <div className="no-complaints">
                <div className="no-complaints-icon">📋</div>
                <h4>No Complaints Assigned</h4>
                <p>You currently have no complaints assigned to you.</p>
                <p>New assignments will appear here when available.</p>
              </div>
            ) : (
              <>
                {/* Desktop Table View */}
                <div className="desktop-table">
                  {/* Table Rows */}
                  {assignedComplaints.map((complaint) => (
                    <div 
                      key={complaint.complaint_id} 
                      className="table-row"
                      onClick={() => handleComplaintClick(complaint.complaint_id)}
                    >
                      <div className="table-col">{complaint.complaint_id ? `#${complaint.complaint_id}` : '#N/A'}</div>
                      <div className="table-col">{complaint.pg_name || 'N/A'}</div>
                      <div className="table-col status-col">
                        {complaint.status ? 
                          complaint.status.replace('_', ' ')
                            .split(' ')
                            .map(word => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase())
                            .join(' ') 
                          : 'N/A'}
                      </div>
                      <div className="table-col">{complaint.type_name || complaint.complaint_type || 'N/A'}</div>
                      <div className="table-col">
                        {complaint.description || 'N/A'}
                      </div>
                      <div className="table-col">{complaint.building_name || 'N/A'}</div>
                      <div className="table-col" onClick={(e) => e.stopPropagation()}>
                        {getStatusButton(complaint)}
                      </div>
                    </div>
                  ))}
                </div>

                {/* Mobile Card View */}
                <div className="mobile-cards">
                  {assignedComplaints.map((complaint) => (
                    <div 
                      key={complaint.complaint_id} 
                      className="mobile-card"
                      onClick={() => handleComplaintClick(complaint.complaint_id)}
                    >
                      <div className="mobile-card-header">
                        <div className="complaint-id">{complaint.complaint_id ? `#${complaint.complaint_id}` : '#N/A'}</div>
                        <div className="complaint-status-mobile">
                          {getStatusButton(complaint)}
                        </div>
                      </div>
                      <div className="mobile-card-body">
                        <div className="mobile-info-row">
                          <span className="mobile-info-item">{complaint.pg_name || 'N/A'}</span>
                          <span className="mobile-info-separator">•</span>
                          <span className="mobile-info-item">{complaint.type_name || complaint.complaint_type || 'N/A'}</span>
                          <span className="mobile-info-separator">•</span>
                          <span className="mobile-info-item">{complaint.building_name || 'N/A'}</span>
                        </div>
                        <div className="mobile-description-row">
                          <span className="mobile-description">{(complaint.description?.length > 30 ? `${complaint.description.substring(0, 30)}...` : complaint.description) || 'N/A'}</span>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              </>
            )}
          </div>
        </div>

        {/* My Team UnAssigned Complaints Section - Full Width */}
        <div className="dashboard-section full-width">
          <div className="section-header">
            <h4 className="section-title">My Team UnAssigned Complaints ({data.total_unassigned_complaints || 0})</h4>
            <button 
              className="view-all-link" 
              onClick={handleViewAllUnassigned}
            >
              View All →
            </button>
          </div>

          <div className="complaints-table">
            {unassignedComplaints.length === 0 ? (
              <div className="no-complaints">
                <div className="no-complaints-icon">✅</div>
                <h4>No Unassigned Complaints</h4>
                <p>Great! All complaints for your team are currently assigned.</p>
                <p>New unassigned complaints will appear here when they arrive.</p>
              </div>
            ) : (
              <>
                {/* Desktop Table View */}
                <div className="desktop-table">
                  {/* Table Rows */}
                  {unassignedComplaints.map((complaint) => (
                    <div 
                      key={complaint.complaint_id} 
                      className="table-row unassigned-row"
                      onClick={() => handleComplaintClick(complaint.complaint_id)}
                    >
                      <div className="table-col">{complaint.complaint_id ? `#${complaint.complaint_id}` : '#N/A'}</div>
                      <div className="table-col">{complaint.pg_name || 'N/A'}</div>
                      <div className="table-col status-col">
                        <span className="complaint-status status-open">Open</span>
                      </div>
                      <div className="table-col">{complaint.type_name || complaint.complaint_type || 'N/A'}</div>
                      <div className="table-col">
                        {complaint.description || 'N/A'}
                      </div>
                      <div className="table-col">{complaint.building_name || 'N/A'}</div>
                      <div className="table-col" onClick={(e) => e.stopPropagation()}>
                        <RTButton 
                          variant="success" 
                          size="small"
                          onClick={(e) => handleAssignToMe(e, complaint)}
                        >
                          Assign to Me
                        </RTButton>
                      </div>
                    </div>
                  ))}
                </div>

                {/* Mobile Card View */}
                <div className="mobile-cards">
                  {unassignedComplaints.map((complaint) => (
                    <div 
                      key={complaint.complaint_id} 
                      className="mobile-card unassigned-card"
                      onClick={() => handleComplaintClick(complaint.complaint_id)}
                    >
                      <div className="mobile-card-header">
                        <div className="complaint-id">{complaint.complaint_id ? `#${complaint.complaint_id}` : '#N/A'}</div>
                        <div className="complaint-status-mobile">
                          <RTButton 
                            variant="success" 
                            size="small"
                            onClick={(e) => handleAssignToMe(e, complaint)}
                          >
                            Assign to Me
                          </RTButton>
                        </div>
                      </div>
                      <div className="mobile-card-body">
                        <div className="mobile-info-row">
                          <span className="mobile-info-item">{complaint.pg_name || 'N/A'}</span>
                          <span className="mobile-info-separator">•</span>
                          <span className="mobile-info-item">{complaint.type_name || complaint.complaint_type || 'N/A'}</span>
                          <span className="mobile-info-separator">•</span>
                          <span className="mobile-info-item">{complaint.building_name || 'N/A'}</span>
                        </div>
                        <div className="mobile-description-row">
                          <span className="mobile-description">{(complaint.description?.length > 30 ? `${complaint.description.substring(0, 30)}...` : complaint.description) || 'N/A'}</span>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              </>
            )}
          </div>
        </div>
      </div>

      {/* Update Complaint Status Modal */}
      <UpdateComplaintStatusModal
        isOpen={showUpdateModal}
        onClose={() => {
          setShowUpdateModal(false);
          setUpdateAction(null);
          setSelectedComplaint(null);
        }}
        complaint={selectedComplaint}
        action={updateAction}
        onSuccess={() => {
          setShowUpdateModal(false);
          setUpdateAction(null);
          setSelectedComplaint(null);
          // Refresh dashboard data if callback is provided
          if (onDataRefresh) {
            onDataRefresh();
          }
        }}
      />

      {/* Assign Complaint Modal */}
      <AssignComplaintModal
        isOpen={assignModalOpen}
        onClose={() => {
          setAssignModalOpen(false);
          setSelectedAssignComplaint(null);
        }}
        complaint={selectedAssignComplaint}
        mode="assignSelf"
        onSuccess={() => {
          setAssignModalOpen(false);
          setSelectedAssignComplaint(null);
          // Refresh dashboard data if callback is provided
          if (onDataRefresh) {
            onDataRefresh();
          }
        }}
      />
    </div>
  );
};

export default StaffDashboard;
